import client from '@/api/client';

/**
 * Who the write endpoints in this module will actually accept — the mirror of
 * `backend/app/routers/routes.py::ROUTE_WRITE_ROLES`, which aliases the single
 * `COORDINATOR_PLUS_ROLES` frozenset in `models/user.py`.
 *
 * Exported so a view that OFFERS one of these actions can decline to render a
 * button the endpoint would only 403. That matters most where a surface is
 * readable by a wider set than it is writable by: `ANALYTICS_READ_ROLES`
 * includes `ANALYST`, this list does not, so an analyst studying the coverage
 * rollup must not be handed a "plan routes" button. A composed button's gate is
 * the INTERSECTION of the steps it composes (memory-bank/plans/easy-buttons.md
 * §2), and this is the narrow half of that intersection.
 *
 * Compare it with `roleMatches()` from `@/lib/roles`, never with a local
 * `new Set([...])` + `.toUpperCase()` — seven such copies are what locked
 * `sms_manager` out of its own seven views after the 0036 rename. This list is
 * the hint; the endpoint stays the authority.
 */
export const ROUTE_WRITE_ROLES = [
  'ADMIN',
  'ORG_MANAGER',
  'MANAGER',
  'COORDINATOR',
] as const;

/**
 * Route creation from a lasso selection (POST /routes/split/preview + /split).
 *
 * Two calls rather than one with a `commit` flag: the split algorithm has
 * exactly one caller (preview), so commit can only ever materialize a grouping
 * the coordinator actually saw. It cannot re-derive one behind their back.
 *
 * This matters more than usual here because routes are effectively write-once —
 * stops cannot be reordered, added, or removed after creation, and there is no
 * DELETE endpoint. The preview IS the only place a mistake is correctable, so
 * everything adjustable has to be adjustable before commit.
 */

export type RouteSplitGroupPreview = {
  name: string;
  door_count: number;
  /**
   * Derived server-side from THIS group's voters, not the whole lasso.
   * Null means the route would be invisible to the Walking-Routes DOORS/TURFS
   * lookup — surfaced so a coordinator can back out while they still can.
   */
  subregion: string | null;
  precinct_num: string | null;
  /** Door-count heuristic. OSRM may refine this down at commit; membership won't change. */
  est_hours: number;
  voter_ids: number[];
};

export type RouteSplitPreviewResponse = {
  total_doors: number;
  route_count: number;
  max_doors_per_route: number;
  groups: RouteSplitGroupPreview[];
};

/** Ask for a proposed split. Writes nothing. */
export async function previewRouteSplit(
  params: {
    voter_ids: number[];
    max_doors_per_route?: number;
    name_prefix?: string;
  },
  signal?: AbortSignal,
): Promise<RouteSplitPreviewResponse> {
  const res = await client.post<RouteSplitPreviewResponse>(
    '/routes/split/preview',
    params,
    { signal },
  );
  return res.data;
}

export type CommittedRoute = {
  id: number;
  name: string;
  door_count: number;
  est_hours: number;
  subregion: string | null;
  status: string;
};

/**
 * Create N routes from an approved grouping. All-or-nothing on the server.
 *
 * Groups accept `{name, voter_ids}` ONLY — the request model is `extra="forbid"`,
 * so posting a preview group back verbatim is a 422. Geography is always
 * re-derived server-side so a client cannot hand back a stale subregion.
 */
export async function commitRouteSplit(
  groups: Array<{ name: string; voter_ids: number[] }>,
  signal?: AbortSignal,
): Promise<CommittedRoute[]> {
  const res = await client.post<CommittedRoute[]>(
    '/routes/split',
    { groups },
    { signal },
  );
  return res.data;
}

/* ---------------------------------------------------------------------------
 * Autoplan — the "Create routes" Easy button
 * (POST /routes/autoplan/preview + /routes/autoplan)
 *
 * Same preview-then-commit shape as the split above, for the same reason:
 * routes are write-once and there is no DELETE, so the preview is the only
 * place a bad batch is correctable.
 *
 * The difference is where the doors come from. The split is handed a selection
 * a human drew; autoplan derives one from the campaign's own districts. That
 * is what lets EVERY field below be optional — `autoplanRoutes({})` is a
 * complete request, which is the whole point of an Easy button.
 * ------------------------------------------------------------------------- */

export type AutoplanRequest = {
  candidate_ids?: number[] | null;
  precinct_nums?: string[] | null;
  max_doors_per_route?: number;
  min_score?: number | null;
  max_routes?: number;
  max_routes_per_precinct?: number | null;
  /**
   * Keep doors that are already on a live route out of the plan. ON by default,
   * and load-bearing: route_stops is unique per (route, voter), not per voter,
   * so without this a second press sends two volunteers to the same house.
   */
  skip_already_routed?: boolean;
  name_prefix?: string | null;
  create_shifts?: boolean;
  /** ISO datetime. Required when create_shifts is true. */
  shift_date?: string | null;
  /** Required (non-empty) when create_shifts is true. Assigned round-robin. */
  assign_to_user_ids?: number[] | null;
};

export type AutoplanGroup = {
  name: string;
  door_count: number;
  precinct_num: string | null;
  subregion: string | null;
  est_hours: number;
  voter_ids: number[];
};

/**
 * Counts that must be RENDERED, not inferred.
 *
 * A district that yields three routes instead of thirty is usually telling you
 * the roll is not geocoded — not that the button is broken. Showing only
 * `route_count` reproduces the dead-honesty failure this codebase keeps
 * flagging (the lasso footer's "0 missing coordinates").
 *
 * The arithmetic always closes:
 *   doors_in_reach = no_coords + already_routed + by_min_score + doors_eligible
 *   doors_eligible = doors_planned + doors_not_planned
 */
export type AutoplanPreviewResponse = {
  route_count: number;
  doors_planned: number;
  doors_in_reach: number;
  doors_excluded_no_coords: number;
  doors_excluded_already_routed: number;
  doors_excluded_by_min_score: number;
  doors_eligible: number;
  doors_not_planned: number;
  coverage_pct: number;
  /** True when max_routes truncated the plan — say so, don't let it look complete. */
  routes_capped: boolean;
  precincts_planned: string[];
  candidate_ids: number[];
  max_doors_per_route: number;
  groups: AutoplanGroup[];
};

export type AutoplanCommitResponse = {
  routes: Array<CommittedRoute & { precinct_num: string | null; assigned_canvasser_id: number | null }>;
  shifts_created: number;
  doors_planned: number;
  doors_in_reach: number;
  doors_excluded_no_coords: number;
  doors_excluded_already_routed: number;
  doors_excluded_by_min_score: number;
  doors_eligible: number;
  doors_not_planned: number;
  coverage_pct: number;
  routes_capped: boolean;
};

/** Propose a batch of walks. Writes nothing. */
export async function previewAutoplan(
  params: AutoplanRequest,
  signal?: AbortSignal,
): Promise<AutoplanPreviewResponse> {
  const res = await client.post<AutoplanPreviewResponse>(
    '/routes/autoplan/preview',
    params,
    { signal },
  );
  return res.data;
}

/**
 * Create the planned routes. All-or-nothing on the server.
 *
 * Re-plans from the same body rather than posting groups back, so pass the
 * SAME params the preview used. Routes are created as drafts.
 */
export async function commitAutoplan(
  params: AutoplanRequest,
  signal?: AbortSignal,
): Promise<AutoplanCommitResponse> {
  const res = await client.post<AutoplanCommitResponse>(
    '/routes/autoplan',
    params,
    { signal },
  );
  return res.data;
}
